// Contrail V3.7.2 — Admin → Email: which provider/sender the platform uses, send a test or a sample account email,
// and the latest account emails (invites, sign-up codes, reset codes) from the activity log.
import { FormEvent, useCallback, useEffect, useState } from "react";
import { adminApi } from "../../lib/admin";
import { supabase } from "../../lib/supabase";
import { Field, Notice, Toolbar, fmtDateTime } from "./ui";

type Status = { provider: "resend" | "mailgun" | null; from: string | null; reply_to: string | null; app_url: string };
type Kind = "test" | "invite" | "welcome" | "reset";
type LogRow = { id: number; at: string; actor_email: string | null; action: string; summary: string };

const KINDS: { v: Kind; label: string; help: string }[] = [
  { v: "test", label: "Plain test email", help: "Short message with the sender details." },
  { v: "invite", label: "Sample invitation", help: "Exactly what a new user receives, with a dummy code." },
  { v: "welcome", label: "Sample sign-up code", help: "Sent by “Re-send invite” to accounts that never signed in." },
  { v: "reset", label: "Sample reset code", help: "What “Email a password-reset code” sends." },
];
const ACTIONS = ["user.invite", "user.reinvite", "user.reset_email", "email.test"];

export default function EmailCheck({ myEmail }: { myEmail: string }) {
  const [st, setSt] = useState<Status | null>(null);
  const [stErr, setStErr] = useState<string | null>(null);
  const [to, setTo] = useState(myEmail);
  const [kind, setKind] = useState<Kind>("test");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<{ ok: boolean; text: string } | null>(null);
  const [log, setLog] = useState<LogRow[] | null>(null);

  const loadLog = useCallback(async () => {
    const { data } = await supabase.from("audit_log").select("id, at, actor_email, action, summary")
      .in("action", ACTIONS).order("at", { ascending: false }).limit(15);
    setLog((data ?? []) as LogRow[]);
  }, []);
  useEffect(() => {
    adminApi<Status>("GET", "email/status").then(setSt).catch((e) => setStErr(e.message));
    loadLog();
  }, [loadLog]);

  async function send(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setRes(null);
    try {
      const r = await adminApi<{ to: string; provider: string; id: string | null }>("POST", "email/test", { to: to.trim(), kind });
      setRes({ ok: true, text: `Sent to ${r.to} via ${r.provider === "resend" ? "Resend" : r.provider}${r.id ? ` (message id ${r.id})` : ""}. Check the inbox in a minute or two, and junk/spam if it isn't there.` });
      loadLog();
    } catch (x: any) { setRes({ ok: false, text: x.message }); }
    finally { setBusy(false); }
  }

  const appWarn = st && !/^https:\/\/fleet\.trackingfiji\.com$/.test(st.app_url);

  return (
    <section>
      <Toolbar>
        <h1>Email</h1>
        <span className="sub">Check that Tracking Fiji emails are being sent and reach the inbox.</span>
      </Toolbar>
      <div className="mail-grid">
        <div className="mail-card">
          <h2>Sending setup</h2>
          {stErr && <Notice kind="bad">{stErr}</Notice>}
          {!st && !stErr && <span className="sub">Loading…</span>}
          {st && (
            <div className="kv">
              <span>Provider</span><b>{st.provider === "resend" ? "Resend" : st.provider === "mailgun" ? "Mailgun (fallback)" : "Not set up"}</b>
              <span>From</span><span className="mono">{st.from ?? "—"}</span>
              <span>Replies go to</span><span className="mono">{st.reply_to ?? "—"}</span>
              <span>Links open</span><span className="mono">{st.app_url}</span>
            </div>
          )}
          {st && st.provider !== "resend" && <Notice kind="warn">Emails are not going through Resend. Set RESEND_API_KEY in Netlify (Functions scope) and redeploy.</Notice>}
          {appWarn && <Notice kind="warn">Links in emails open {st!.app_url}. Set APP_URL to https://fleet.trackingfiji.com in Netlify when fleet is the live address.</Notice>}
          <p className="sub">Used for invitations, sign-up and reset codes sent from Users, plus alert, reminder and admin notification emails. The “Forgot password” page is sent by Supabase through the same Resend domain.</p>
        </div>

        <form className="mail-card" onSubmit={send}>
          <h2>Send a test</h2>
          <Field label="Send to" wide><input type="email" value={to} onChange={(e) => setTo(e.target.value)} required /></Field>
          <fieldset className="aradio">
            <legend>What to send</legend>
            {KINDS.map((k) => (
              <label key={k.v}><input type="radio" name="kind" checked={kind === k.v} onChange={() => setKind(k.v)} />
                <span><b>{k.label}</b> — {k.help}</span></label>
            ))}
          </fieldset>
          {res && <Notice kind={res.ok ? "ok" : "bad"} onClose={() => setRes(null)}>{res.text}</Notice>}
          <div><button className="btn-primary" disabled={busy || !st?.provider}>{busy ? "Sending…" : "Send test email"}</button></div>
          <p className="sub">Samples are marked “[Sample]” and the code in them is not real. Try an Outlook address and a Gmail address.</p>
        </form>

        <div className="mail-card">
          <h2>Recent account emails</h2>
          {log === null && <span className="sub">Loading…</span>}
          {log && !log.length && <span className="sub">None yet.</span>}
          {log && log.length > 0 && (
            <ul className="mail-log">
              {log.map((r) => <li key={r.id}><span>{r.summary}</span><span className="sub mono">{fmtDateTime(r.at)}</span></li>)}
            </ul>
          )}
          <p className="sub">“Sent” means the provider accepted it. Delivery to the inbox is shown in the Resend dashboard → Emails.</p>
        </div>

        <div className="mail-card">
          <h2>If an email lands in junk</h2>
          <ol className="mail-steps">
            <li>Open it and mark it <b>Not junk</b> (Outlook) or <b>Not spam</b> (Gmail).</li>
            <li>In Gmail: <b>⋮ → Show original</b> — SPF, DKIM and DMARC should all say PASS.</li>
            <li>Microsoft 365 quarantine: release it and report it as a false positive in Defender.</li>
            <li>Ask the recipient to add <span className="mono">noreply@notify.trackingfiji.com</span> to their contacts or safe senders.</li>
          </ol>
        </div>
      </div>
    </section>
  );
}
